/**
 * page-types/configs.js - Page Type Configurations
 * Location: /app/frontend/src/admin/page_builder/page-types/
 * 
 * Defines default section stacks and allowed sections per page type.
 * This is the canonical configuration for page layouts.
 */

import { PageType, PageTypeLabel, PageTypeDescription, PageTypeIcon } from './types';

/**
 * Template categories for allowedSections filtering
 * Maps to template IDs within each category
 */
export const CATEGORY_TEMPLATES = {
  hero: ['hero_overlay', 'hero_split', 'hero_minimal'],
  content: ['trending_rail', 'featured_grid', 'new_releases_rail', 'continue_reading', 'genre_spotlight', 'community_picks', 'art_gallery_grid'],
  reader: ['chapter_list', 'vertical_reader_stack', 'horizontal_reader', 'page_by_page_reader'],
  creator: ['creator_spotlight', 'creator_grid', 'creator_cta', 'leaderboard_strip'],
  cta: ['cta_banner', 'transparency_footer', 'announcement_banner', 'category_spotlight'],
  gallery: ['art_gallery_grid'],
};

/**
 * Get template IDs for specified categories
 * @param {string[]} categories - Category names
 * @returns {string[]} Template IDs
 */
function getTemplatesForCategories(categories) {
  const templates = new Set();
  categories.forEach(cat => {
    (CATEGORY_TEMPLATES[cat] || []).forEach(t => templates.add(t));
  });
  return Array.from(templates);
}

/**
 * Page Type Configurations
 * Each config defines defaults and constraints for that page type
 */
export const PAGE_TYPE_CONFIGS = {
  // ============================================================
  // DISCOVERY & NAVIGATION
  // ============================================================
  
  [PageType.HOMEPAGE]: {
    id: PageType.HOMEPAGE,
    name: PageTypeLabel[PageType.HOMEPAGE],
    description: PageTypeDescription[PageType.HOMEPAGE],
    icon: PageTypeIcon[PageType.HOMEPAGE],
    
    // Default sections spawned on page create
    defaultSections: [
      'hero_overlay',
      'trending_rail',
      'featured_grid',
      'new_releases_rail',
      'creator_grid',
    ],
    
    // Allowed categories (explicit, no drift)
    allowedCategories: ['hero', 'content', 'creator', 'cta'],
    allowedSections: getTemplatesForCategories(['hero', 'content', 'creator', 'cta']),
    
    // Guardrails
    minSections: 1,
    maxSections: 15,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  [PageType.DISCOVERY]: {
    id: PageType.DISCOVERY,
    name: PageTypeLabel[PageType.DISCOVERY],
    description: PageTypeDescription[PageType.DISCOVERY],
    icon: PageTypeIcon[PageType.DISCOVERY],
    
    defaultSections: [
      'hero_minimal',
      'trending_rail',
      'genre_spotlight',
      'community_picks',
    ],
    
    allowedCategories: ['hero', 'content', 'creator', 'cta'],
    allowedSections: getTemplatesForCategories(['hero', 'content', 'creator', 'cta']),
    
    minSections: 1,
    maxSections: 12,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  // ============================================================
  // CREATOR PAGES
  // ============================================================
  
  [PageType.CREATOR_PROFILE]: {
    id: PageType.CREATOR_PROFILE,
    name: PageTypeLabel[PageType.CREATOR_PROFILE],
    description: PageTypeDescription[PageType.CREATOR_PROFILE],
    icon: PageTypeIcon[PageType.CREATOR_PROFILE],
    
    defaultSections: [
      'creator_spotlight',
      'art_gallery_grid', // Portfolio/gallery
      'creator_grid', // Related creators or their series
      'transparency_footer',
    ],
    
    allowedCategories: ['creator', 'content', 'cta', 'gallery'],
    allowedSections: getTemplatesForCategories(['creator', 'content', 'cta', 'gallery']),
    
    minSections: 1,
    maxSections: 10,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  // ============================================================
  // CONTENT PAGES
  // ============================================================
  
  [PageType.COMIC_SERIES]: {
    id: PageType.COMIC_SERIES,
    name: PageTypeLabel[PageType.COMIC_SERIES],
    description: PageTypeDescription[PageType.COMIC_SERIES],
    icon: PageTypeIcon[PageType.COMIC_SERIES],
    
    defaultSections: [
      'hero_overlay',
      'chapter_list',
      'creator_spotlight',
      'community_picks', // Related series
    ],
    
    allowedCategories: ['hero', 'content', 'creator', 'cta'],
    allowedSections: [...getTemplatesForCategories(['hero', 'content', 'creator', 'cta']), 'chapter_list'],
    
    minSections: 2, // At least hero + chapter list
    maxSections: 10,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  [PageType.NOVEL_SERIES]: {
    id: PageType.NOVEL_SERIES,
    name: PageTypeLabel[PageType.NOVEL_SERIES],
    description: PageTypeDescription[PageType.NOVEL_SERIES],
    icon: PageTypeIcon[PageType.NOVEL_SERIES],
    
    defaultSections: [
      'hero_minimal',
      'chapter_list',
      'transparency_footer',
    ],
    
    allowedCategories: ['hero', 'content', 'cta'],
    allowedSections: [...getTemplatesForCategories(['hero', 'content', 'cta']), 'chapter_list'],
    
    minSections: 2,
    maxSections: 8,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  [PageType.GALLERY]: {
    id: PageType.GALLERY,
    name: PageTypeLabel[PageType.GALLERY],
    description: PageTypeDescription[PageType.GALLERY],
    icon: PageTypeIcon[PageType.GALLERY],
    
    defaultSections: [
      'hero_minimal',
      'art_gallery_grid',
      'transparency_footer',
    ],
    
    allowedCategories: ['hero', 'content', 'cta', 'gallery'],
    allowedSections: getTemplatesForCategories(['hero', 'content', 'cta', 'gallery']),
    
    minSections: 1,
    maxSections: 8,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  // ============================================================
  // READER PRESENTATIONS (Locked single-section)
  // ============================================================
  
  [PageType.READER_VERTICAL]: {
    id: PageType.READER_VERTICAL,
    name: PageTypeLabel[PageType.READER_VERTICAL],
    description: PageTypeDescription[PageType.READER_VERTICAL],
    icon: PageTypeIcon[PageType.READER_VERTICAL],
    
    defaultSections: ['vertical_reader_stack'],
    
    allowedCategories: [],
    allowedSections: [], // No sections can be added
    
    // Reader guardrails: locked single section
    minSections: 1,
    maxSections: 1,
    allowReorder: false,
    allowAddRemove: false,
    isReader: true,
  },
  
  [PageType.READER_HORIZONTAL]: {
    id: PageType.READER_HORIZONTAL,
    name: PageTypeLabel[PageType.READER_HORIZONTAL],
    description: PageTypeDescription[PageType.READER_HORIZONTAL],
    icon: PageTypeIcon[PageType.READER_HORIZONTAL],
    
    defaultSections: ['horizontal_reader'],
    
    allowedCategories: [],
    allowedSections: [],
    
    minSections: 1,
    maxSections: 1,
    allowReorder: false,
    allowAddRemove: false,
    isReader: true,
  },
  
  [PageType.READER_PAGE_BY_PAGE]: {
    id: PageType.READER_PAGE_BY_PAGE,
    name: PageTypeLabel[PageType.READER_PAGE_BY_PAGE],
    description: PageTypeDescription[PageType.READER_PAGE_BY_PAGE],
    icon: PageTypeIcon[PageType.READER_PAGE_BY_PAGE],
    
    defaultSections: ['page_by_page_reader'],
    
    allowedCategories: [],
    allowedSections: [],
    
    minSections: 1,
    maxSections: 1,
    allowReorder: false,
    allowAddRemove: false,
    isReader: true,
  },
  
  // ============================================================
  // RANKINGS & COMMUNITY
  // ============================================================
  
  [PageType.LEADERBOARD]: {
    id: PageType.LEADERBOARD,
    name: PageTypeLabel[PageType.LEADERBOARD],
    description: PageTypeDescription[PageType.LEADERBOARD],
    icon: PageTypeIcon[PageType.LEADERBOARD],
    
    defaultSections: [
      'hero_split',
      'leaderboard_strip',
      'creator_grid',
    ],
    
    allowedCategories: ['hero', 'creator', 'content', 'cta'],
    allowedSections: getTemplatesForCategories(['hero', 'creator', 'content', 'cta']),
    
    minSections: 1,
    maxSections: 8,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  [PageType.COLLABORATION]: {
    id: PageType.COLLABORATION,
    name: PageTypeLabel[PageType.COLLABORATION],
    description: PageTypeDescription[PageType.COLLABORATION],
    icon: PageTypeIcon[PageType.COLLABORATION],
    
    defaultSections: [
      'creator_spotlight',
      'creator_grid',
      'cta_banner',
    ],
    
    allowedCategories: ['creator', 'content', 'cta'],
    allowedSections: getTemplatesForCategories(['creator', 'content', 'cta']),
    
    minSections: 1,
    maxSections: 10,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
  
  // ============================================================
  // UTILITY PAGES
  // ============================================================
  
  [PageType.STATIC]: {
    id: PageType.STATIC,
    name: PageTypeLabel[PageType.STATIC],
    description: PageTypeDescription[PageType.STATIC],
    icon: PageTypeIcon[PageType.STATIC],
    
    defaultSections: [
      'hero_minimal',
      'transparency_footer',
    ],
    
    // Limited sections for static pages
    allowedCategories: ['hero', 'cta'],
    allowedSections: ['hero_minimal', 'hero_overlay', 'transparency_footer', 'cta_banner', 'announcement_banner'],
    
    minSections: 1,
    maxSections: 6,
    allowReorder: true,
    allowAddRemove: true,
    isReader: false,
  },
};

/**
 * Get config for a page type
 * @param {string} pageType - PageType value
 * @returns {Object|null} Page type config
 */
export function getPageTypeConfig(pageType) {
  return PAGE_TYPE_CONFIGS[pageType] || null;
}

/**
 * Get default sections for a page type
 * @param {string} pageType - PageType value
 * @returns {string[]} Template IDs
 */
export function getDefaultSections(pageType) {
  const config = getPageTypeConfig(pageType);
  return config?.defaultSections || [];
}

/**
 * Get allowed sections for a page type
 * @param {string} pageType - PageType value
 * @returns {string[]} Template IDs
 */
export function getAllowedSections(pageType) {
  const config = getPageTypeConfig(pageType);
  return config?.allowedSections || [];
}

/**
 * Check if a template can be added to a page type
 * @param {string} pageType - PageType value
 * @param {string} templateId - Template ID to check
 * @returns {boolean}
 */
export function canAddTemplate(pageType, templateId) {
  const config = getPageTypeConfig(pageType);
  if (!config) return false;
  if (!config.allowAddRemove) return false;
  return config.allowedSections.includes(templateId);
}

/**
 * Validate section count for a page type
 * @param {string} pageType - PageType value
 * @param {number} count - Current section count
 * @returns {{valid: boolean, message?: string}}
 */
export function validateSectionCount(pageType, count) {
  const config = getPageTypeConfig(pageType);
  if (!config) return { valid: false, message: 'Unknown page type' };
  
  if (count < config.minSections) {
    return { 
      valid: false, 
      message: `Minimum ${config.minSections} section(s) required` 
    };
  }
  
  if (count > config.maxSections) {
    return { 
      valid: false, 
      message: `Maximum ${config.maxSections} sections allowed` 
    };
  }
  
  return { valid: true };
}

export default {
  PAGE_TYPE_CONFIGS,
  CATEGORY_TEMPLATES,
  getPageTypeConfig,
  getDefaultSections,
  getAllowedSections,
  canAddTemplate,
  validateSectionCount,
};
